import type { Theme } from 'hephaestus/core';
import { Box, Typography } from 'hephaestus/web';
import { useTranslation } from 'react-i18next';
import { useFormatMoney } from '../hooks/useFormatMoney';
import {
  LABEL_SX,
  TABLE_HEAD_SX,
  TABULAR_SX,
  TRUNCATE_SX,
  VALUE_ROW_SX,
} from '../styles';
import type { SubTradeRequest } from '../types';
import { isBuyType } from '../utils';
export type PortfolioRowDetailPendingTradesTableProps = {
  pendingTrades: SubTradeRequest[];
  theme: Theme;
};

export const PortfolioRowDetailPendingTradesTable = ({
  pendingTrades,
  theme,
}: PortfolioRowDetailPendingTradesTableProps) => {
  const { t } = useTranslation('portfolioTradingHub');
  const formatMoney = useFormatMoney();

  const columns = [
    {
      label: t('rowDetail.pendingTrades.side'),
      width: '18%',
      align: 'left' as const,
    },
    {
      label: t('rowDetail.pendingTrades.security'),
      width: '20%',
      align: 'left' as const,
    },
    {
      label: t('rowDetail.pendingTrades.amount'),
      width: '20%',
      align: 'right' as const,
    },
    {
      label: t('rowDetail.pendingTrades.qty'),
      width: '14%',
      align: 'right' as const,
    },
    {
      label: t('rowDetail.pendingTrades.state'),
      width: '14%',
      align: 'center' as const,
    },
    {
      label: t('rowDetail.pendingTrades.created'),
      width: '14%',
      align: 'right' as const,
    },
  ];

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          px: 1.5,
          py: 0.5,
          bgcolor: theme.color.neutralHover,
          borderBottom: '1px solid',
          borderColor: theme.color.outlineVariant,
        }}
      >
        {columns.map((col) => (
          <Typography
            key={col.label}
            variant='labelSmall'
            sx={{
              ...TABLE_HEAD_SX,
              width: col.width,
              fontWeight: 700,
              letterSpacing: '0.04em',
              color: theme.color.onSurfaceVariant,
              textAlign: col.align,
            }}
          >
            {col.label}
          </Typography>
        ))}
      </Box>
      <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
        {pendingTrades.map((trade) => {
          const isBuy = isBuyType(trade.type);
          const sideColor = isBuy ? theme.color.positive : theme.color.negative;
          const stateColor =
            trade.state === 'REQUESTED'
              ? theme.color.positive
              : trade.state === 'READY'
                ? theme.color.primary
                : theme.color.warning;
          return (
            <Box
              key={trade.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 1.5,
                py: 0.5,
                borderBottom: '1px solid',
                borderColor: theme.color.outlineVariant,
                '&:last-child': { borderBottom: 'none' },
                '&:hover': { bgcolor: theme.color.neutralHover },
              }}
            >
              <Box
                sx={{
                  width: '18%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: sideColor,
                    color: theme.color.onPrimary,
                    ...LABEL_SX,
                    fontWeight: 800,
                  }}
                >
                  {isBuy ? '↑' : '↓'}
                </Box>
                <Typography
                  variant='bodySmall'
                  sx={{ ...VALUE_ROW_SX, color: sideColor }}
                >
                  {trade.type}
                </Typography>
              </Box>
              <Box sx={{ width: '20%' }}>
                <Typography
                  variant='bodySmall'
                  component='div'
                  sx={{ fontWeight: 600, lineHeight: 1.2 }}
                >
                  {trade.financialProduct?.ticker || '—'}
                </Typography>
                <Typography
                  variant='labelSmall'
                  component='div'
                  sx={{
                    color: theme.color.onSurfaceVariant,
                    lineHeight: 1.2,
                    ...TRUNCATE_SX,
                  }}
                >
                  {trade.financialProduct?.translatedName?.en || ''}
                </Typography>
              </Box>
              <Typography
                variant='bodySmall'
                sx={{
                  ...VALUE_ROW_SX,
                  width: '20%',
                  textAlign: 'right',
                }}
              >
                {formatMoney(Math.abs(trade.moneyAllocatedCents || 0))}
              </Typography>
              <Typography
                variant='bodySmall'
                sx={{
                  ...TABULAR_SX,
                  width: '14%',
                  textAlign: 'right',
                  color: theme.color.onSurfaceVariant,
                }}
              >
                {trade.expectedQuantity
                  ? trade.expectedQuantity.toFixed(2)
                  : '—'}
              </Typography>
              <Box
                sx={{
                  width: '14%',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant='labelSmall'
                  sx={{
                    px: 0.75,
                    py: 0.15,
                    borderRadius: '4px',
                    ...TABLE_HEAD_SX,
                    fontWeight: 700,
                    bgcolor: stateColor,
                    color: theme.color.onPrimary,
                    letterSpacing: '0.04em',
                  }}
                >
                  {trade.state}
                </Typography>
              </Box>
              <Typography
                variant='labelSmall'
                sx={{
                  width: '14%',
                  textAlign: 'right',
                  color: theme.color.onSurfaceVariant,
                }}
              >
                {trade.createdAt
                  ? new Date(trade.createdAt).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                    })
                  : '—'}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </>
  );
};
